import React, { useContext } from 'react';
import { Icon } from 'venn-ui-kit';
import { analyticsService } from 'venn-utils';
import { DataUploaderMode } from '../types';
import { uploadConfig } from '../utils';
import { getIconForFormat } from './Icons';
import type { CombinedSample } from './CombinedSampleData';
import { FileTypes } from './SampleData';
import UniversalUploaderContext from '../../../contexts/universal-uploader';
import { cn } from '@/lib/utils';

interface SampleCardRefreshedProps {
  sample: CombinedSample;
  mode: DataUploaderMode;
}

interface DownloadButtonProps {
  tag: string;
  type: FileTypes;
  index: number;
  total: number;
  refreshedStyling: boolean;
  onDownload: (tag: string) => void;
}

const fileTypeLabel: Record<FileTypes, string> = {
  [FileTypes.EXCEL]: 'EXCEL',
  [FileTypes.CSV]: '.CSV',
  [FileTypes.PDF]: '.PDF',
};

const DownloadButton = ({ tag, type, index, total, refreshedStyling, onDownload }: DownloadButtonProps) => {
  const borderRadius =
    index === 0 && index === total - 1
      ? '4px'
      : index === 0
        ? '4px 0 0 4px'
        : index === total - 1
          ? '0 4px 4px 0'
          : '0';
  const marginLeft = index === 0 ? 0 : -1;

  return (
    <a
      target="_blank"
      href={`/api/file/tag/${tag}`}
      rel="noreferrer"
      className={cn(
        'flex flex-row items-center justify-center gap-1 min-w-[125px] min-h-9 px-3 py-1.5 font-bold text-sm border border-solid hover:bg-venn-grey-10 hover:z-10',
        refreshedStyling ? 'text-venn-dark-blue' : 'text-venn-primary-dark',
        refreshedStyling ? 'border-venn-dark-blue' : 'border-venn-primary-dark',
      )}
      style={{ borderRadius, marginLeft }}
      onClick={() => onDownload(tag)}
    >
      <Icon type="download" />
      {fileTypeLabel[type]}
    </a>
  );
};

const FlatSampleCard = ({ sample, mode }: SampleCardRefreshedProps) => {
  const { refreshedStyling } = useContext(UniversalUploaderContext);

  return (
    <div
      data-testid="qa-flat-sample-card"
      className="w-full bg-white border border-solid border-venn-grey-30 rounded overflow-hidden flex flex-row justify-between mb-2.5"
    >
      <div className="flex justify-start items-center w-[175px] min-w-[175px] h-auto overflow-hidden text-xl p-1">
        {getIconForFormat(sample.layout)}
      </div>
      <div className="pt-2 pr-5 pb-2 pl-4 flex-1 flex flex-col">
        <div className="flex flex-row items-baseline justify-between">
          <h3 className="flex flex-row items-center gap-[10px] text-[16px] font-bold">{sample.title}</h3>
          {sample.frequency && (
            <span className="text-[12px] font-roboto font-normal text-black">{sample.frequency.join(', ')}</span>
          )}
        </div>
        <p className="text-[12px] text-venn-grey-80 mt-[2px]">{sample.description}</p>
        {sample.info && (
          <div className="flex flex-row justify-end">
            <span className="text-venn-grey-80 text-sm">
              <Icon prefix="far" type="circle-info" />
              &nbsp;
              {sample.info}
            </span>
          </div>
        )}
        <div className="flex flex-row mt-auto">
          {sample.download.map(({ tag, type }, index) => (
            <DownloadButton
              key={tag}
              tag={tag}
              type={type}
              index={index}
              total={sample.download.length}
              refreshedStyling={refreshedStyling}
              onDownload={(tag: string) =>
                analyticsService.fileTemplateDownloaded({
                  dataType: uploadConfig[mode]?.dataType,
                  isPrivatesTemplate: mode === DataUploaderMode.Privates,
                  tag,
                })
              }
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default FlatSampleCard;
